import React from 'react';
import { Card } from '../ui/Card';
import { formatCurrency } from '../../utils/pdfGenerator';
import { FinancialStats } from '../../services/financialService';
import './Financial.css';

interface FinancialDashboardProps {
  stats: FinancialStats | null;
  loading: boolean;
  showExpanded?: boolean;
  onCardClick?: (action: string, filters?: any) => void;
}

export const FinancialDashboard: React.FC<FinancialDashboardProps> = ({
  stats,
  loading,
  showExpanded = true,
  onCardClick,
}) => {
  if (loading) {
    return (
      <div className="ns-grid ns-grid-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} title="Carregando...">
            <div className="ns-kpi">
              <span className="ns-kpi-value">...</span>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Nenhum dado financeiro disponível.</p>
      </div>
    );
  }

  return (
    <div className="ns-grid ns-grid-3">
      <Card
        title="Receita Total"
        subtitle="Todas as consultas pagas"
        onClick={() => onCardClick?.('receita-total', { type: 'receita' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{formatCurrency(stats.receitaTotal)}</span>
          <span className="ns-kpi-label">
            {stats.consultasPagas} consulta{stats.consultasPagas !== 1 ? 's' : ''} paga{stats.consultasPagas !== 1 ? 's' : ''}
          </span>
        </div>
      </Card>

      <Card
        title="Receita do Mês"
        subtitle="Consultas pagas este mês"
        onClick={() => onCardClick?.('receita-mes', { type: 'receita', period: 'month' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{formatCurrency(stats.receitaMes)}</span>
          <span className="ns-kpi-label">Mês atual</span>
        </div>
      </Card>

      <Card
        title="Receita do Ano"
        subtitle="Consultas pagas este ano"
        onClick={() => onCardClick?.('receita-ano', { type: 'receita', period: 'year' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{formatCurrency(stats.receitaAno)}</span>
          <span className="ns-kpi-label">Ano atual</span>
        </div>
      </Card>

      <Card
        title="Média por Consulta"
        subtitle="Valor médio das consultas pagas"
        onClick={() => onCardClick?.('consultas-pagas', { status: 'pago' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{formatCurrency(stats.mediaPorConsulta)}</span>
          <span className="ns-kpi-label">Por consulta</span>
        </div>
      </Card>

      <Card
        title="Consultas Pagas"
        subtitle="Total de consultas com pagamento confirmado"
        onClick={() => onCardClick?.('consultas-pagas', { status: 'pago' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{stats.consultasPagas}</span>
          <span className="ns-kpi-label">Confirmadas</span>
        </div>
      </Card>

      <Card
        title="Consultas Pendentes"
        subtitle="Aguardando pagamento"
        onClick={() => onCardClick?.('consultas-pendentes', { status: 'pendente' })}
        style={{ cursor: onCardClick ? 'pointer' : 'default' }}
      >
        <div className="ns-kpi">
          <span className="ns-kpi-value">{stats.consultasPendentes}</span>
          <span className="ns-kpi-label">Pendentes</span>
        </div>
      </Card>

      {showExpanded && stats.despesaTotal !== undefined && (
        <>
          <Card
            title="Despesa Total"
            subtitle="Todas as despesas pagas"
            onClick={() => onCardClick?.('despesa-total', { type: 'despesa' })}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span className="ns-kpi-value" style={{ color: '#ef4444' }}>
                {formatCurrency(stats.despesaTotal || 0)}
              </span>
              <span className="ns-kpi-label">Total de despesas</span>
            </div>
          </Card>

          <Card
            title="Despesa do Mês"
            subtitle="Despesas pagas este mês"
            onClick={() => onCardClick?.('despesa-mes', { type: 'despesa', period: 'month' })}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span className="ns-kpi-value" style={{ color: '#ef4444' }}>
                {formatCurrency(stats.despesaMes || 0)}
              </span>
              <span className="ns-kpi-label">Mês atual</span>
            </div>
          </Card>

          <Card
            title="Despesa do Ano"
            subtitle="Despesas pagas este ano"
            onClick={() => onCardClick?.('despesa-ano', { type: 'despesa', period: 'year' })}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span className="ns-kpi-value" style={{ color: '#ef4444' }}>
                {formatCurrency(stats.despesaAno || 0)}
              </span>
              <span className="ns-kpi-label">Ano atual</span>
            </div>
          </Card>

          <Card
            title="Saldo Líquido"
            subtitle="Receitas - Despesas (Total)"
            onClick={() => onCardClick?.('saldo-total')}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span
                className="ns-kpi-value"
                style={{
                  color: (stats.saldoLiquido || 0) >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {formatCurrency(stats.saldoLiquido || 0)}
              </span>
              <span className="ns-kpi-label">Saldo total</span>
            </div>
          </Card>

          <Card
            title="Saldo do Mês"
            subtitle="Receitas - Despesas (Mês atual)"
            onClick={() => onCardClick?.('saldo-mes', { period: 'month' })}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span
                className="ns-kpi-value"
                style={{
                  color: (stats.saldoLiquidoMes || 0) >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {formatCurrency(stats.saldoLiquidoMes || 0)}
              </span>
              <span className="ns-kpi-label">Mês atual</span>
            </div>
          </Card>

          <Card
            title="Saldo do Ano"
            subtitle="Receitas - Despesas (Ano atual)"
            onClick={() => onCardClick?.('saldo-ano', { period: 'year' })}
            style={{ cursor: onCardClick ? 'pointer' : 'default' }}
          >
            <div className="ns-kpi">
              <span
                className="ns-kpi-value"
                style={{
                  color: (stats.saldoLiquidoAno || 0) >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {formatCurrency(stats.saldoLiquidoAno || 0)}
              </span>
              <span className="ns-kpi-label">Ano atual</span>
            </div>
          </Card>

          {stats.totalTransacoes !== undefined && (
            <Card
              title="Total de Transações"
              subtitle="Todas as transações registradas"
              onClick={() => onCardClick?.('transacoes-total')}
              style={{ cursor: onCardClick ? 'pointer' : 'default' }}
            >
              <div className="ns-kpi">
                <span className="ns-kpi-value">{stats.totalTransacoes || 0}</span>
                <span className="ns-kpi-label">Transações</span>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};


